import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Activity, 
  User, 
  FileText, 
  Upload, 
  MessageSquare, 
  Settings, 
  Tag, 
  ArrowRight,
  Clock,
  RefreshCw,
  Filter,
  Eye
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import api from '../utils/api';


const API = "/api";

const ActivityDashboard = ({ onProductSelect }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const { toast } = useToast();
  const abortControllerRef = useRef(null);

  const activityIcons = {
    product_created: { icon: FileText, color: 'text-green-600', bg: 'bg-green-100' },
    product_updated: { icon: Settings, color: 'text-blue-600', bg: 'bg-blue-100' },
    product_deleted: { icon: FileText, color: 'text-red-600', bg: 'bg-red-100' },
    note_added: { icon: MessageSquare, color: 'text-purple-600', bg: 'bg-purple-100' },
    file_uploaded: { icon: Upload, color: 'text-orange-600', bg: 'bg-orange-100' },
    stage_changed: { icon: ArrowRight, color: 'text-indigo-600', bg: 'bg-indigo-100' },
    priority_changed: { icon: Settings, color: 'text-yellow-600', bg: 'bg-yellow-100' },
    user_tagged: { icon: Tag, color: 'text-pink-600', bg: 'bg-pink-100' },
    message_sent: { icon: MessageSquare, color: 'text-teal-600', bg: 'bg-teal-100' },
    user_updated: { icon: User, color: 'text-cyan-600', bg: 'bg-cyan-100' }
  };

  const filterOptions = [
    { value: 'all', label: 'All Activities' },
    { value: 'product_created', label: 'Products Created' },
    { value: 'note_added', label: 'Notes Added' },
    { value: 'file_uploaded', label: 'Files Uploaded' },
    { value: 'stage_changed', label: 'Stage Changes' },
    { value: 'priority_changed', label: 'Priority Changes' },
    { value: 'user_tagged', label: 'User Tags' },
    { value: 'message_sent', label: 'Messages' },
    { value: 'user_updated', label: 'User Updates' }
  ];

  const fetchWithRetry = async (requestFn, maxRetries = 3) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;
        
        // Don't retry on cancellation
        if (error.name === 'CanceledError') {
          throw error;
        }
        
        // Don't retry on last attempt
        if (attempt === maxRetries) {
          break;
        }
        
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
    
    throw lastError;
  };

  const fetchActivities = async () => {
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setLoading(true);
    
    try {
      const response = await fetchWithRetry(async () => {
        return await api.get('/dashboard/activities', {
          params: { limit: 100 },
          signal: abortControllerRef.current.signal
        });
      });
      
      setActivities(response.data);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        toast({
          title: "Error",
          description: "Failed to fetch activities. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  useEffect(() => {
    fetchActivities();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchActivities, 30000);
    return () => {
      clearInterval(interval);
      // Cancel any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleActivityClick = (activity) => {
    if (activity.product_id && onProductSelect) {
      onProductSelect(activity.product_id);
    }
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  const getActivityIcon = (activityType) => {
    const config = activityIcons[activityType] || activityIcons.product_updated;
    return config;
  };

  const filteredActivities = filter === 'all' 
    ? activities 
    : activities.filter(activity => activity.activity_type === filter);

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="space-y-3">
          {/* Title row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-slate-900">Activity Feed</h3>
              <Badge variant="outline" className="text-xs">
                {filteredActivities.length} items
              </Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchActivities}
              disabled={loading}
              className="flex items-center gap-1"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>

          {/* Filter row - full width on mobile */}
          <div className="flex items-center justify-between sm:justify-start">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="text-sm border border-slate-200 rounded px-2 py-1 outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
            >
              {filterOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-sm text-slate-600">
          Real-time activity feed across all departments
        </p>
      </CardHeader>
      
      <CardContent>
        {loading && activities.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-slate-400 mr-2" />
            <span className="text-slate-600">Loading activities...</span>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-slate-900 mb-2">
              No activities yet
            </h4>
            <p className="text-slate-600">
              {filter === 'all' 
                ? 'Activities will appear here as users interact with the system'
                : `No ${filterOptions.find(o => o.value === filter)?.label.toLowerCase()} found`
              }
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {filteredActivities.map((activity) => {
              const iconConfig = getActivityIcon(activity.activity_type);
              const IconComponent = iconConfig.icon;
              const isClickable = activity.product_id && onProductSelect;
              
              return (
                <div
                  key={activity.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors ${
                    isClickable ? 'cursor-pointer hover:shadow-sm' : ''
                  }`}
                  onClick={() => isClickable && handleActivityClick(activity)}
                >
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full ${iconConfig.bg} flex items-center justify-center`}>
                    <IconComponent className={`w-4 h-4 ${iconConfig.color}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 mb-1">
                          {activity.message}
                        </p>
                        
                        {/* Activity metadata */}
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <User className="w-3 h-3" />
                          <span>{activity.user_name}</span>
                          <Clock className="w-3 h-3 ml-2" />
                          <span>{formatRelativeTime(activity.created_at)}</span>
                          
                          {activity.product_title && (
                            <>
                              <FileText className="w-3 h-3 ml-2" />
                              <span className="truncate max-w-32" title={activity.product_title}>
                                {activity.product_title}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {isClickable && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    
                    {/* Additional metadata for specific activity types */}
                    {activity.metadata && (
                      <div className="mt-2 text-xs">
                        {activity.activity_type === 'file_uploaded' && activity.metadata.filename && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                            📎 {activity.metadata.filename}
                          </Badge>
                        )}
                        
                        {activity.activity_type === 'user_tagged' && activity.metadata.tagged_users && (
                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                            🏷️ {activity.metadata.tagged_users.length} user(s) tagged
                          </Badge>
                        )}
                        
                        {(activity.activity_type === 'priority_changed' || activity.activity_type === 'stage_changed') && (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">
                              {activity.metadata.old_priority || activity.metadata.old_stage}
                            </span>
                            <ArrowRight className="w-3 h-3 text-slate-400" />
                            <span className="text-slate-700 font-medium">
                              {activity.metadata.new_priority || activity.metadata.new_stage}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityDashboard;